import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

const agencySans = "font-[Calibri,'Calibri_Light','Segoe_UI',sans-serif]";

/** Long-form case study for the Neevii platform shown on agency partnerships. */
const ROLE_ITEMS = [
  'product direction',
  'UX and interface design',
  'workflow architecture',
  'responsive systems',
  'front-end implementation',
  'visual systems and branding',
  'iterative product refinement',
];

/**
 * Larger/taller agency example modal — full-width hero graphic (title lives in artwork),
 * scrollable editorial footer on white/grey typography.
 */
export function NeeviiExampleModal({ open, onClose }) {
  const closeBtnRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    const t = window.setTimeout(() => closeBtnRef.current?.focus(), 0);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.clearTimeout(t);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" aria-hidden={!open}>
      <button
        type="button"
        className="absolute inset-0 bg-black/65 backdrop-blur-[2px]"
        aria-label="Close preview"
        onClick={onClose}
      />
      <div className="relative z-[101] flex max-h-[100dvh] min-h-full items-start justify-center overflow-y-auto px-3 py-4 sm:p-6 sm:py-8">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="neevii-example-title"
          className={`portal-modal-panel relative my-auto flex max-h-[min(94dvh,1680px)] w-full flex-col overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-2xl ${agencySans}`}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            ref={closeBtnRef}
            type="button"
            className="absolute right-3 top-3 z-10 rounded-full border border-neutral-200 bg-white p-2 text-neutral-600 shadow-sm transition-colors hover:bg-neutral-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#cc9600] sm:right-4 sm:top-4"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-5 w-5" strokeWidth={1.75} aria-hidden />
          </button>

          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <h2 id="neevii-example-title" className="sr-only">
              Neevii — Operational accounting platform
            </h2>

            {/* Modal artwork only — flush top/sides inside rounded shell; prose sits on white below */}
            <img
              src="/assets/images/Neevii_modal.png"
              alt="Neevii — Smart Accounting, Invoicing, and HMRC-ready workflows for UK small businesses; desktop dashboard and mobile guidance UI"
              width={2400}
              height={1350}
              className="block h-auto w-full max-w-none rounded-none border-0 bg-transparent align-top"
              loading="eager"
              decoding="async"
            />

            <div className="bg-white px-4 py-7 text-neutral-900 sm:px-6 sm:py-9 md:px-8 lg:px-10">
              <div className="w-full space-y-6 text-base leading-relaxed sm:text-[1.05rem] sm:leading-[1.65] lg:text-[1.0625rem]">
                <p className="text-neutral-950">
                  Neevii is an end-to-end operational accounting platform designed around workflow clarity, guided user
                  flows, and responsive product systems.
                </p>
                <p className="text-neutral-600">
                  The project involved designing and building dashboards, reporting systems, filing workflows,
                  transaction management, invoicing, AI-assisted guidance, and responsive desktop/mobile interfaces
                  within a single operational ecosystem.
                </p>
                <p className="text-neutral-600">
                  A major focus of the project was simplifying complex accounting and compliance workflows into
                  interfaces that feel structured, approachable, and operationally clear rather than overwhelming. The
                  product evolved through continuous refinement of hierarchy, navigation, workflow visibility, and
                  responsive layout systems.
                </p>
                <div>
                  <p className="font-medium text-neutral-950">
                    My role covered the full product lifecycle including:
                  </p>
                  <ul className="mt-3 list-disc space-y-2 pl-6 text-neutral-600 marker:text-neutral-400">
                    {ROLE_ITEMS.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </div>
                <p className="text-neutral-600">
                  The overall goal was to create operational software that feels modern, trustworthy, and easy to
                  navigate while still handling complex workflows and structured business data.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NeeviiExampleModal;
